import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { Plus, Loader2, CheckCircle2, Circle, ListTodo, ExternalLink } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Checkbox } from "@/components/ui/checkbox";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Pill, Dot } from "@/components/app/shared";
import { api, errMsg, fmtDate, fmtRel } from "@/lib/api";
import { cn } from "@/lib/utils";

function NewTaskDialog({ open, onOpenChange, entity, entityId, assignToSelf = true }) {
  const qc = useQueryClient();
  const [title, setTitle] = useState("");
  const [notes, setNotes] = useState("");
  const [due, setDue] = useState("");
  const create = useMutation({
    mutationFn: async () => (await api.post("/tasks", { title, notes, due: due ? new Date(`${due}T15:00:00Z`).toISOString() : null, entity, entityId })).data,
    onSuccess: (t) => {
      qc.invalidateQueries({ queryKey: ["tasks"] });
      if (t.googleSync === "ok") toast.success("Tarea creada y sincronizada con Google Tasks");
      else if (t.googleSync === "not_connected") toast.success("Tarea creada (conecta Google Tasks para sincronizar)");
      else toast.success("Tarea creada");
      setTitle(""); setNotes(""); setDue("");
      onOpenChange(false);
    },
    onError: (e) => toast.error(errMsg(e)),
  });
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="new-task-dialog">
        <DialogHeader>
          <DialogTitle>Nueva tarea</DialogTitle>
          <DialogDescription>Si conectaste Google Tasks, aparecerá también en la app de Tasks de {assignToSelf ? "tu cuenta" : "la persona asignada"}.</DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <div className="grid gap-1.5">
            <Label className="text-xs">Título</Label>
            <Input value={title} onChange={(e) => setTitle(e.target.value)} placeholder="Qué hay que hacer" data-testid="task-title-input" />
          </div>
          <div className="grid gap-1.5">
            <Label className="text-xs">Notas</Label>
            <Textarea rows={3} value={notes} onChange={(e) => setNotes(e.target.value)} data-testid="task-notes-input" />
          </div>
          <div className="grid gap-1.5">
            <Label className="text-xs">Fecha</Label>
            <Input type="date" value={due} onChange={(e) => setDue(e.target.value)} className="w-[180px]" data-testid="task-due-input" />
          </div>
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={() => onOpenChange(false)}>Cancelar</Button>
          <Button onClick={() => create.mutate()} disabled={!title.trim() || create.isPending} data-testid="task-create-button">
            {create.isPending ? <Loader2 className="mr-1.5 h-4 w-4 animate-spin" /> : <Plus className="mr-1.5 h-4 w-4" />} Crear tarea
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export default function TasksWidget({ entity, entityId, title = "Mis tareas", scope = "mine" }) {
  const qc = useQueryClient();
  const [open, setOpen] = useState(false);
  const key = ["tasks", scope, entity || "all", entityId || "all"];
  const { data: tasks = [], isLoading } = useQuery({
    queryKey: key,
    queryFn: async () => {
      const params = new URLSearchParams();
      if (entity && entityId) { params.set("entity", entity); params.set("entityId", entityId); }
      else params.set("scope", scope);
      return (await api.get(`/tasks?${params}`)).data;
    },
  });
  const toggle = useMutation({
    mutationFn: async ({ id, status }) => (await api.patch(`/tasks/${id}`, { status })).data,
    onMutate: async ({ id, status }) => {
      await qc.cancelQueries({ queryKey: key });
      const prev = qc.getQueryData(key);
      qc.setQueryData(key, (old = []) => old.map((t) => t.id === id ? { ...t, status } : t));
      return { prev };
    },
    onError: (e, _v, ctx) => { if (ctx?.prev) qc.setQueryData(key, ctx.prev); toast.error(errMsg(e)); },
    onSettled: () => qc.invalidateQueries({ queryKey: key }),
  });
  const del = useMutation({
    mutationFn: async (id) => api.delete(`/tasks/${id}`),
    onSuccess: () => qc.invalidateQueries({ queryKey: key }),
  });

  const open_ = tasks.filter((t) => t.status === "OPEN");
  const done_ = tasks.filter((t) => t.status === "DONE");

  return (
    <section className="rounded-lg border border-border bg-card" data-testid="tasks-widget">
      <div className="flex items-center justify-between border-b border-border px-5 py-3.5">
        <div className="flex items-center gap-2 text-sm font-semibold"><ListTodo className="h-4 w-4" strokeWidth={1.75} /> {title}</div>
        <Button size="sm" onClick={() => setOpen(true)} data-testid="tasks-add-button"><Plus className="mr-1.5 h-3.5 w-3.5" /> Nueva</Button>
      </div>
      <div className="max-h-[320px] overflow-y-auto">
        {isLoading ? (
          <div className="flex items-center justify-center p-6 text-sm text-muted-foreground"><Loader2 className="mr-2 h-4 w-4 animate-spin" /> Cargando…</div>
        ) : tasks.length === 0 ? (
          <div className="px-5 py-6 text-center text-sm text-muted-foreground" data-testid="tasks-empty">
            Sin tareas. Agrega una con «Nueva» o crea desde una historia o deal.
          </div>
        ) : (
          <ul className="divide-y divide-border" data-testid="tasks-list">
            {[...open_, ...done_].map((t) => {
              const overdue = t.status === "OPEN" && t.due && t.due.slice(0, 10) < new Date().toISOString().slice(0, 10);
              return (
                <li key={t.id} className="flex items-start gap-3 px-5 py-3" data-testid="task-row">
                  <Checkbox
                    checked={t.status === "DONE"}
                    onCheckedChange={(v) => toggle.mutate({ id: t.id, status: v ? "DONE" : "OPEN" })}
                    className="mt-0.5"
                    data-testid="task-check"
                  />
                  <div className="min-w-0 flex-1">
                    <div className={cn("text-[13px]", t.status === "DONE" && "text-muted-foreground line-through")}>{t.title}</div>
                    {t.notes && <div className="text-[11.5px] text-muted-foreground">{t.notes.split("\n")[0].slice(0, 120)}</div>}
                    <div className="mt-0.5 flex flex-wrap items-center gap-x-2 gap-y-1 font-mono text-[10.5px] text-muted-foreground">
                      {t.assigneeName && <span>{t.assigneeName}</span>}
                      {t.due && <span className={overdue ? "text-bad" : undefined}>· {fmtDate(t.due)}</span>}
                      {t.googleTaskId && <Pill tone="mint" className="ml-1"><Dot tone="ok" /> en Google Tasks</Pill>}
                      {t.link && <a href={t.link} className="ml-1 inline-flex items-center gap-0.5 hover:underline"><ExternalLink className="h-3 w-3" />ir</a>}
                    </div>
                  </div>
                  <Button variant="ghost" size="icon" className="h-7 w-7 opacity-60 hover:opacity-100" aria-label="Eliminar" onClick={() => del.mutate(t.id)} data-testid="task-delete">
                    {t.status === "DONE" ? <CheckCircle2 className="h-3.5 w-3.5" /> : <Circle className="h-3.5 w-3.5" />}
                  </Button>
                </li>
              );
            })}
          </ul>
        )}
      </div>
      <NewTaskDialog open={open} onOpenChange={setOpen} entity={entity} entityId={entityId} />
    </section>
  );
}
